// Where a dragged edge lands.
//
// At seven-day zoom one pixel is about six minutes, so dragging alone cannot express a
// boundary to the second. Snapping is what makes the drag usable at all: the candidates are
// the instants that are actually meaningful, and everything else is reached with the numeric
// inputs beside the canvas rather than by aiming harder.

export type SnapKind = "icy" | "scheduled" | "segment" | "grid";

export interface SnapTarget {
  ms: number;
  kind: SnapKind;
}

export interface SnapResult {
  ms: number;
  kind: SnapKind | null;
}

export interface SnapSources {
  /** Instants the station's own metadata changed. The closest thing to ground truth. */
  icy?: readonly number[];
  /** Scheduled event edges. Written in whole hours and routinely minutes out. */
  scheduled?: readonly number[];
  /** Edges of other segments already on screen. */
  segments?: readonly number[];
  /** Spacing of the visible gridlines, so an edge can be put on a round instant. */
  gridStepMs?: number;
}

/**
 * How the kinds break a tie.
 *
 * Only consulted when two candidates are the same distance away, which happens constantly:
 * a segment edge normally sits exactly on the ICY instant it was detected from. Reporting it
 * as ICY is the honest answer, and it is what the UI should tell the person dragging.
 */
const PRIORITY: Record<SnapKind, number> = {
  icy: 0,
  scheduled: 1,
  segment: 2,
  grid: 3,
};

/** Round instants near `ms`, at the spacing of the visible gridlines. */
function gridNear(ms: number, stepMs: number): number[] {
  if (!Number.isFinite(stepMs) || stepMs <= 0) return [];
  const below = Math.floor(ms / stepMs) * stepMs;
  return [below, below + stepMs];
}

/**
 * Move `ms` to the nearest meaningful instant within `toleranceMs`, or leave it alone.
 *
 * Returns the kind that won as well as the instant, because the UI says which -- "snapped to
 * a title change" and "snapped to the schedule" are very different claims about how much to
 * trust the resulting boundary.
 */
export function snapTime(ms: number, sources: SnapSources, toleranceMs: number): SnapResult {
  if (toleranceMs <= 0) return { ms, kind: null };

  const candidates: SnapTarget[] = [];
  for (const value of sources.icy ?? []) candidates.push({ ms: value, kind: "icy" });
  for (const value of sources.scheduled ?? []) candidates.push({ ms: value, kind: "scheduled" });
  for (const value of sources.segments ?? []) candidates.push({ ms: value, kind: "segment" });
  if (sources.gridStepMs) {
    for (const value of gridNear(ms, sources.gridStepMs)) {
      candidates.push({ ms: value, kind: "grid" });
    }
  }

  let best: SnapTarget | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const distance = Math.abs(candidate.ms - ms);
    if (distance > toleranceMs) continue;
    if (
      distance < bestDistance ||
      (distance === bestDistance && best !== null && PRIORITY[candidate.kind] < PRIORITY[best.kind])
    ) {
      best = candidate;
      bestDistance = distance;
    }
  }

  return best ? { ms: best.ms, kind: best.kind } : { ms, kind: null };
}

/** The minimum a segment may be shrunk to, so a drag cannot invert or annihilate it. */
export const MIN_SPAN_MS = 1000;

export interface Span {
  startMs: number;
  endMs: number;
}

/**
 * Apply a dragged edge to a span.
 *
 * The opposite edge is the constraint: dragging a start past its own end would produce an
 * empty range, which Postgres normalises to `empty` -- bounds become NULL and the comparison
 * checks silently pass, so the row survives as something no query can find. Clamping here
 * means that shape never reaches the API.
 */
export function applyEdge(span: Span, edge: "start" | "end", ms: number): Span {
  if (edge === "start") {
    return { startMs: Math.min(ms, span.endMs - MIN_SPAN_MS), endMs: span.endMs };
  }
  return { startMs: span.startMs, endMs: Math.max(ms, span.startMs + MIN_SPAN_MS) };
}

/** Which edge of a block a pointer is grabbing, if either. */
export function edgeAt(x: number, x0: number, x1: number, grabPx: number): "start" | "end" | null {
  const startDistance = Math.abs(x - x0);
  const endDistance = Math.abs(x - x1);
  if (Math.min(startDistance, endDistance) > grabPx) return null;
  // A block narrower than twice the grab radius has overlapping handles. Picking the nearer
  // one keeps both reachable instead of letting the start swallow the whole block.
  return startDistance <= endDistance ? "start" : "end";
}
